import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import { transformCategoryTemplateWhatsApp } from 'src/utils/meta.utils';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v1_actionWboTemplatesCategoryLogs from 'src/actions/v1/meta/wbo/v1_actionWboTemplatesCategoryLogs';
import v1_actionWboTemplatesCategoryLogsMarcarVistos from 'src/actions/v1/meta/wbo/v1_actionWboTemplatesCategoryLogsMarcarVistos';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { TEMPLATES_WHATSAPP_DESIGN as D } from './TemplatesWhatsapp.design';

export default function TemplatesCategoryLogs() {
  const navigate = useNavigate();
  const { user, cliente } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [canais, setCanais] = useState([]);
  const [canalFiltro, setCanalFiltro] = useState('all');
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });

  const clienteId = cliente?._id || user?.clienteId;

  const buscarCanais = useCallback(async () => {
    if (!clienteId) return;
    try {
      const response = await actionListaCanaisPorCliente(clienteId);
      const listaCanais = response?.data?.data || response?.data?.canais || [];
      const canaisWb = listaCanais.filter((c) => c.platform === 'whatsapp_business');
      setCanais(canaisWb);

      v1_actionWboTemplatesCategoryLogsMarcarVistos(clienteId).catch(() => {});
    } catch (err) {
      console.error('Erro ao buscar canais:', err);
    }
  }, [clienteId]);

  const buscarLogs = useCallback(async (page = 1) => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (canalFiltro !== 'all') params.canalId = canalFiltro;

      const res = await v1_actionWboTemplatesCategoryLogs(clienteId, params);
      const data = res.data?.data;
      setLogs(data?.logs || []);
      setPagination(data?.pagination || { current: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
      setLogs([]);
      setPagination({ current: 1, pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [clienteId, canalFiltro]);

  useEffect(() => {
    buscarCanais();
  }, [buscarCanais]);

  useEffect(() => {
    buscarLogs(1);
  }, [buscarLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const categoryBadge = (category) => {
    const tone =
      category === 'MARKETING' ? D.categoryMarketing :
      category === 'AUTHENTICATION' ? D.categoryAuth :
      D.categoryUtility;
    return (
      <span className={`${D.badge} ${tone}`}>
        {transformCategoryTemplateWhatsApp(category) || category || '-'}
      </span>
    );
  };

  return (
    <div className={D.root} style={{ fontFamily: D.fontStack }}>
      <div className={D.stickyHeader}>
        <div className={D.container}>
          <div className={D.topbar}>
            <div className={D.topbarIdentity}>
              <button
                type="button"
                onClick={() => navigate('/dashboard/configuracoes/templates-whatsapp')}
                className={D.backButton}
              >
                <Iconify icon="eva:arrow-back-outline" width={18} />
              </button>
              <div className={D.iconTile}>
                <Iconify icon="eva:file-text-outline" width={22} />
              </div>
              <div className="min-w-0">
                <h1 className={D.title}>Logs de categoria</h1>
                <p className={D.subtitle}>
                  Histórico de alterações de categoria dos templates pela Meta.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`${D.container} pt-6 ${D.pageBody}`}>
        <div className={D.stack}>
          <div className={D.sectionHeader}>
            <div className={D.sectionHeaderMain}>
              <h2 className={D.sectionHeaderTitle}>Filtros</h2>
              <p className={D.sectionHeaderSub}>
                Filtre por canal para ver só as mudanças relevantes.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="w-64">
                <Select value={canalFiltro} onValueChange={setCanalFiltro}>
                  <SelectTrigger className={D.select}>
                    <SelectValue placeholder="Todos os canais" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os canais</SelectItem>
                    {canais.map((canal) => (
                      <SelectItem key={canal._id} value={String(canal._id)}>
                        {canal.nome || canal.sessao || canal._id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                className={D.ghostButton}
                onClick={() => buscarLogs(1)}
                disabled={loading || !clienteId}
              >
                Atualizar
              </button>
              {pagination.total > 0 && (
                <span className="text-[12px] text-[#a1a1aa]">
                  {pagination.total} registro{pagination.total !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {loading && (
            <div className={D.loadingBar}>
              <div className={D.loadingBarInner} style={{ width: '66%' }} />
            </div>
          )}

          <div className={D.sectionHeader}>
            <div className={D.sectionHeaderMain}>
              <h2 className={D.sectionHeaderTitle}>Histórico</h2>
              <p className={D.sectionHeaderSub}>
                Cada linha mostra a categoria anterior e a nova atribuída pela Meta.
              </p>
            </div>
          </div>

          {logs.length > 0 ? (
            <div className={D.cardFlush}>
              <div className={D.tableWrap}>
                <GenericTable
                  headLabels={[
                    { id: 'date', label: 'Data', className: 'w-44 pl-4' },
                    { id: 'template', label: 'Template', className: 'flex-1' },
                    { id: 'from', label: 'Categoria anterior', className: 'w-44' },
                    { id: 'arrow', label: '', className: 'w-10 text-center' },
                    { id: 'to', label: 'Categoria nova', className: 'w-44' },
                    { id: 'channel', label: 'Canal', className: 'w-44 pr-4' },
                  ]}
                >
                  {logs.map((log) => (
                    <div key={log._id} className={GENERIC_TABLE_DESIGN.row.container}>
                      <div className={`w-44 pl-4 ${D.tableCellMuted}`}>
                        {formatDate(log.createdAt)}
                      </div>

                      <div className="flex-1 min-w-[200px] pr-4">
                        <h3 className={`${D.tableCell} font-semibold`}>
                          {log.templateName || log.templateId?.templateName || log.templateId?.name || '-'}
                        </h3>
                        <span className="text-[11px] text-[#a1a1aa] font-mono">
                          {log.metaTemplateId || ''}
                        </span>
                      </div>

                      <div className="w-44">
                        {categoryBadge(log.categoryAnterior)}
                      </div>

                      <div className="w-10 flex justify-center text-[#a1a1aa]">
                        <Iconify icon="eva:arrow-forward-outline" width={18} />
                      </div>

                      <div className="w-44">
                        {categoryBadge(log.categoryNova)}
                      </div>

                      <div className={`w-44 pr-4 ${D.tableCellMuted} truncate`}>
                        {log.canalId?.nome || '-'}
                      </div>
                    </div>
                  ))}
                </GenericTable>
              </div>
            </div>
          ) : (
            !loading && (
              <div className={`${D.card} ${D.emptyState}`}>
                <div className={D.emptyIcon}>
                  <Iconify icon="eva:file-text-outline" width={28} />
                </div>
                <h3 className={D.emptyTitle}>Nenhum log encontrado</h3>
                <p className={D.emptyDesc}>
                  Nenhuma alteração de categoria foi registrada ainda. Os logs serão criados automaticamente quando a Meta alterar a categoria de um template.
                </p>
              </div>
            )
          )}

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2.5">
              <button
                type="button"
                className={D.iconButton}
                disabled={pagination.current <= 1}
                onClick={() => buscarLogs(pagination.current - 1)}
              >
                <Iconify icon="eva:arrow-back-outline" width={16} />
              </button>
              <span className="text-[13px] text-[#71717a]">
                Página {pagination.current} de {pagination.pages}
              </span>
              <button
                type="button"
                className={D.iconButton}
                disabled={pagination.current >= pagination.pages}
                onClick={() => buscarLogs(pagination.current + 1)}
              >
                <Iconify icon="eva:arrow-forward-outline" width={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
